import { useCallback, useMemo, useState } from "react";
import { SafeImage } from "@components/SafeImage";
import type { components } from "@apis/generated/openapi";
import { twMerge } from "tailwind-merge";

type UserProductImage = components["schemas"]["UserProductImage"];

interface UIProductSubImagesProps {
  images: UserProductImage[];
  className?: string;
}

/**
 * 商品副图 hover 浮层：默认隐藏，hover 卡片时浮现于主图底部。
 * 断图各自不展示；当全部副图都断图（或无副图）时整个浮层不渲染，避免出现空遮罩框。
 */
export const UIProductSubImages = ({ images, className }: UIProductSubImagesProps) => {
  // 记录每个索引的加载结果：true=成功，false=断图，缺省=尚未确定
  const [resolved, setResolved] = useState<Record<number, boolean>>({});

  const handleResolved = useCallback((index: number, ok: boolean) => {
    setResolved((prev) => (prev[index] === ok ? prev : { ...prev, [index]: ok }));
  }, []);

  const allBroken = useMemo(() => {
    if (images.length === 0) return true;
    return images.every((_, index) => resolved[index] === false);
  }, [images, resolved]);

  if (allBroken) return null;

  return (
    <div
      className={twMerge(
        "absolute bottom-[12px] right-[12px] items-center gap-[4px] rounded-[8px] bg-kds-mask p-[4px]",
        className,
      )}
    >
      {images.map((image, index) => (
        <SafeImage
          key={`${image.url}-${index}`}
          src={image.url}
          className="size-[56px] shrink-0 rounded-[4px]"
          onResolved={(ok) => handleResolved(index, ok)}
        />
      ))}
    </div>
  );
};
